import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT_SCORM_FEATURE_TOKEN } from '@campus/environment';
import { ReplaySubject } from 'rxjs';
import { CrossFrameLMS, Scorm12API, StringKeyMap } from 'scorm-again';
import {
  ScormApiInterface,
  ScormCmiInterface,
  ScormCmiMode,
  ScormErrorCodes,
  ScormStatus,
} from './scorm-api.interface';

@Injectable({
  providedIn: 'root',
})
export class ScormAgainApi implements ScormApiInterface {
  commit$ = new ReplaySubject<string>(1);
  cmi$ = new ReplaySubject<string>(1);

  mode: ScormCmiMode = ScormCmiMode.CMI_MODE_NORMAL;
  eduContentId?: number | null;
  hasCalledLMSFinish = false;

  get lastErrorCode() {
    return this.api.lmsGetLastError() as ScormErrorCodes;
  }
  set lastErrorCode(value: ScormErrorCodes) {
    if (value === ScormErrorCodes.NO_ERROR) {
      this.api.clearSCORMError('');
    } else {
      this.api.throwSCORMError(undefined, +value);
    }
  }
  get lastDiagnosticMessage() {
    return this.LMSGetErrorString(this.lastErrorCode);
  }

  private api: Scorm12API;
  private scormConfig = inject(ENVIRONMENT_SCORM_FEATURE_TOKEN, { optional: true });

  constructor() {
    // https://jcputney.github.io/scorm-again/docs/configuration/settings-reference#settings-table
    this.api = new Scorm12API({
      logLevel: 'ERROR', // 'DEBUG' | 'INFO' | 'WARN' | 'ERROR'
    });
    this.patchLessonModeValidation();

    // Bridge child-frame postMessages to `this.api` (cross domain)
    new CrossFrameLMS(this.api, this.scormConfig?.cors?.allowedOrigin || '*');
  }

  setCurrentCMI(cmi: string) {
    this.reset();

    if (cmi) {
      try {
        const _cmi: Omit<ScormCmiInterface, 'mode'> & StringKeyMap = JSON.parse(cmi);
        // override mode, as it is determined by the LMS, not the exercise
        _cmi.core.lesson_mode = this.mode;
        delete _cmi.mode;
        // _cmi.core.entry = 'resume';
        this.api.loadFromJSON(_cmi);
      } catch {
        this.lastErrorCode = ScormErrorCodes.NOT_INITIALIZED_ERROR;
        return;
      }
    } else {
      this.api.loadFromJSON(this.getNewCmi());
    }

    this.hasCalledLMSFinish = false;
    this.cmi$.next(JSON.stringify(this.api.cmi));
  }

  LMSInitialize() {
    return this.api.lmsInitialize() as 'true' | 'false';
  }
  LMSFinish() {
    const scormResult = this.api.lmsFinish() as 'true' | 'false';

    // non-standard return value, compliant with ScormApi implementation
    if (!ScormStatus.isCompleted(this.api.cmi.core.lesson_status as ScormStatus)) return 'false';

    return scormResult;
  }
  LMSGetValue(p: string) {
    return this.api.lmsGetValue(p);
  }
  LMSSetValue(p: string, v: any) {
    return this.api.lmsSetValue(p, v) as 'true' | 'false';
  }
  LMSCommit() {
    return this.api.lmsCommit() as 'true' | 'false';
  }
  LMSGetLastError() {
    return this.api.lmsGetLastError() as ScormErrorCodes;
  }
  LMSGetDiagnostic() {
    return this.api.lmsGetDiagnostic(this.lastErrorCode);
  }
  /**
   * Get a short description for the specified errorCode
   *
   * @param {ScormErrorCodes} code code that identifies the error message
   * @returns {String} CMIErrorMessage
   * @memberof ScormApi
   */
  LMSGetErrorString(code: ScormErrorCodes): string {
    switch (code) {
      case ScormErrorCodes.NO_ERROR:
        return 'Geen vuiltje aan de lucht...';
      case ScormErrorCodes.INVALID_ARGUMENT_ERROR:
        return 'Foutief argument:';
      case ScormErrorCodes.ELEMENT_CANNOT_HAVE_CHILDREN_ERROR:
        return 'Dit element heeft geen _children';
      case ScormErrorCodes.ELEMENT_CANNOT_HAVE_COUNT_ERROR:
        return 'Dit element heeft geen _count';
      case ScormErrorCodes.NOT_INITIALIZED_ERROR:
        return 'De oefening werd niet correct opgestart';
      case ScormErrorCodes.NOT_IMPLEMENTED_ERROR:
        return 'Deze feature werd niet geïmplementeerd door het LMS';
      case ScormErrorCodes.INVALID_SET_VALUE_ELEMENT_IS_KEYWORD_ERROR:
        return 'Je kan geen waardes zetten voor keywords';
      case ScormErrorCodes.READ_ONLY_ERROR:
        return 'Dit element is alleen-lezen';
      case ScormErrorCodes.WRITE_ONLY_ERROR:
        return 'Dit element is alleen-schrijven';
      case ScormErrorCodes.INCORRECT_DATA_TYPE_ERROR:
        return 'De waarde die je wil schrijven is niet geldig';
      default:
        // return this.api.lmsGetErrorString(code);
        return 'Algemene fout:';
    }
  }

  private reset() {
    this.lastErrorCode = ScormErrorCodes.NO_ERROR;

    // api.reset() takes care of cleaning up event listeners, and some cmi fields (core.score, suspend_data),
    // but preserves persistent fields (suspend_data, lesson_location) according to "SCORM resume" semantics.
    this.api.reset();

    // Additionally, `loadFromJSON` skips falsy values (empty string ""),
    // so we need to manually reset these fields to ensure a clean slate for new attempts
    this.api.cmi.suspend_data = '';
    this.api.cmi.core.lesson_location = '';

    // Emit scorm-again events onto streams
    this.api.on('LMSCommit', () => {
      if (this.canUpdate()) this.commit$.next(JSON.stringify(this.api.cmi));
    });
    this.api.on('LMSSetValue.cmi.*', (CMIElement, value) => {
      if (CMIElement === 'cmi.core.lesson_status' && value === ScormStatus.STATUS_COMPLETED) {
        // toggle hasCalledLMSFinish so time is updated through ScormExerciseService.updateResult
        this.hasCalledLMSFinish = true;
      }
      if (this.canUpdate()) this.cmi$.next(JSON.stringify(this.api.cmi));
    });
    this.api.on('LMSFinish', () => {
      this.hasCalledLMSFinish = true;
      if (this.canUpdate()) this.commit$.next(JSON.stringify(this.api.cmi));
    });
  }

  private getNewCmi(): Omit<ScormCmiInterface, 'mode'> & StringKeyMap {
    return {
      core: {
        score: {
          raw: '0',
          min: undefined,
          max: undefined,
        },
        lesson_mode: this.mode,
        lesson_location: '',
        lesson_status: ScormStatus.STATUS_INCOMPLETE,
        total_time: '0000:00:00',
        session_time: '0000:00:00',
      },
      objectives: Array.from({ length: 10 }, () => ({
        score: {
          raw: '0',
          min: undefined,
          max: undefined,
          scale: undefined,
        },
        status: ScormStatus.STATUS_INCOMPLETE,
      })),
      suspend_data: '',
    };
  }

  private canUpdate(): boolean {
    return ![ScormCmiMode.CMI_MODE_PREVIEW, ScormCmiMode.CMI_MODE_REVIEW].includes(this.mode);
  }

  private patchLessonModeValidation() {
    // We use non-spec modes (preview/grading), but scorm-again hard-validates cmi.core.lesson_mode
    // against ^(normal|browse|review)$.

    // As a workaround, we override `get/set lesson_mode` from `node_modules/scorm-again/src/cmi/scorm12/core.ts`.
    // api.reset() reuses the same `.core` object, so overriding it once after instantiation is sufficient.
    const core = this.api.cmi.core;
    const validLessonModes = Object.values(ScormCmiMode);

    const lessonModeSetter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(core), 'lesson_mode')?.set;
    if (!lessonModeSetter) {
      throw new Error(
        'ScormAgainApi: expected a prototype setter for cmi.core.lesson_mode. Scorm-again internals changed!'
      );
    }

    Object.defineProperty(core, 'lesson_mode', {
      get(): ScormCmiMode {
        return this._lesson_mode;
      },
      set(lesson_mode: ScormCmiMode) {
        // use the original setter to throw a Scorm12ValidationError(READ_ONLY_ELEMENT) if `lesson_mode` is
        // set after initialization (lesson_mode is read-only after initialization, per SCORM 1.2 spec)
        if (this.initialized) return lessonModeSetter.call(this, lesson_mode);
        if (!validLessonModes.includes(lesson_mode)) return lessonModeSetter.call(this, lesson_mode);
        this._lesson_mode = lesson_mode; // store the value, bypassing validation
      },
      configurable: true,
      enumerable: true,
    });
  }
}
